import {
  getAmazonCountryCodeFromClientData,
  getCampaignListBudgetRule,
  roundCampaignListBudget,
} from "../../campaignList/campaignListV2BudgetConfig";
import {
  formatSpBidBound,
  getMaxSpBid,
  getMinSpBid,
  getSpBidCurrencyCode,
  getSpBidRule,
} from "./bidCapsConfig";
import { DAY_NAMES, DAY_NAMES_DISPLAY } from "./constants";

/** @deprecated Prefer getMinSpBid — kept for call-site compatibility. */
export function getMinCampaignBudget() {
  return getMinSpBid();
}

export function getMaxCampaignBid() {
  return getMaxSpBid();
}

export function getCampaignBudgetCurrencyCode() {
  return getSpBidCurrencyCode() || getCampaignListBudgetRule().currencyCode;
}

/** Same source as campaign-list-v2: profile currency/country via Intl. */
export function getCampaignBudgetCurrencySymbol() {
  const currencyCode = getCampaignBudgetCurrencyCode();
  const countryCode = getAmazonCountryCodeFromClientData() || "IN";
  const localeCountry = countryCode === "UK" ? "GB" : countryCode;
  try {
    const formatter = new Intl.NumberFormat(`en-${localeCountry}`, {
      style: "currency",
      currency: currencyCode,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });
    const symbolPart = formatter.formatToParts(1).find((p) => p.type === "currency");
    return symbolPart?.value || currencyCode;
  } catch {
    return currencyCode;
  }
}

export function getCampaignBudgetNumberLocale() {
  const countryCode = getAmazonCountryCodeFromClientData() || "IN";
  return `en-${countryCode === "UK" ? "GB" : countryCode}`;
}

export function roundCampaignBudget(value) {
  return roundCampaignListBudget(value);
}

export function generateId() {
  return `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 11)}`;
}

export function computeProjectedBudget(base, changeType, changeValue) {
  const baseNum = Number(base) || 0;
  const v = changeValue === "" || changeValue == null ? NaN : Number(changeValue);
  if (Number.isNaN(v)) return baseNum;
  if (changeType === "ABSOLUTE") return v;
  if (changeType === "PERCENT_DECREASE") return baseNum * (1 - v / 100);
  return baseNum * (1 + v / 100);
}

export function parseNumOrEmpty(val) {
  if (val === "" || val == null) return "";
  const n = Number(val);
  return Number.isNaN(n) ? "" : n;
}

/** Ensures value is at least SP min bid (default when empty). */
export function enforceMinBudgetFloor(value) {
  const minBid = getMinSpBid();
  if (value === "" || value == null) return minBid;
  const num = Number(value);
  if (Number.isNaN(num)) return minBid;
  return Math.max(minBid, num);
}

function isBidCapDecimalPatternValid(value, scale) {
  const trimmed = String(value).trim();
  if (scale === 0) return /^-?\d+$/.test(trimmed);
  return new RegExp(`^-?\\d+(\\.\\d{1,${scale}})?$`).test(trimmed);
}

/** Value must be within SP min/max bid range and within marketplace decimal scale. */
export function isBidCapValueValid(value) {
  if (value === "" || value == null) return false;
  const num = Number(value);
  if (Number.isNaN(num)) return false;
  const { minBid, maxBid, scale } = getSpBidRule();
  if (!isBidCapDecimalPatternValid(value, scale)) return false;
  return num >= minBid && num <= maxBid;
}

export function isBudgetFloorValid(value) {
  return isBidCapValueValid(value);
}

export function isMinBudgetValid(value) {
  return isBidCapValueValid(value);
}

export function isMaxBudgetValid(value) {
  return isBidCapValueValid(value);
}

export function isDailyBudgetValid(value) {
  return isBidCapValueValid(value);
}

/**
 * Empty is allowed while editing; invalid numeric / out-of-SP-range / bad decimals return a message.
 * @param {"lower"|"upper"} kind
 */
function getBidCapFieldError(value, kind) {
  if (value === "" || value == null) return "";
  const num = Number(value);
  if (Number.isNaN(num)) return "Enter a valid number";

  const { minBid, maxBid, currencyCode, scale } = getSpBidRule();
  if (!isBidCapDecimalPatternValid(value, scale)) {
    return scale === 0
      ? "Enter a whole number (no decimals)"
      : `Use at most ${scale} decimal places`;
  }

  const label = kind === "upper" ? "Upper bid cap" : "Lower bid cap";
  const minLabel = formatSpBidBound(minBid);
  const maxLabel = formatSpBidBound(maxBid);

  if (num < minBid) {
    return `${label} must be at least ${minLabel} ${currencyCode}`;
  }
  if (num > maxBid) {
    return `${label} must be at most ${maxLabel} ${currencyCode}`;
  }
  return "";
}

export function getMinBudgetError(value) {
  return getBidCapFieldError(value, "lower");
}

export function getMaxBudgetError(value) {
  return getBidCapFieldError(value, "upper");
}

export function getDailyBudgetError(value) {
  return getBidCapFieldError(value, "lower");
}

/** Slot Value is a budget amount only when Budget Type is Number. */
export function getAbsoluteBudgetValueError(value, changeType) {
  if (changeType !== "ABSOLUTE") return "";
  return getBidCapFieldError(value, "lower");
}

export const BUDGET_BULK_MODE = {
  COPY_CAMPAIGN_BUDGET: "COPY_CAMPAIGN_BUDGET",
  INCREASE_PERCENT: "INCREASE_PERCENT",
  DECREASE_PERCENT: "DECREASE_PERCENT",
  SET_ABSOLUTE: "SET_ABSOLUTE",
};

/** @deprecated Use BUDGET_BULK_MODE */
export const MAX_BUDGET_BULK_MODE = BUDGET_BULK_MODE;

export const BUDGET_BULK_MODE_OPTIONS = [
  { value: BUDGET_BULK_MODE.INCREASE_PERCENT, label: "Increase by %" },
  { value: BUDGET_BULK_MODE.DECREASE_PERCENT, label: "Decrease by %" },
  { value: BUDGET_BULK_MODE.SET_ABSOLUTE, label: "Set an absolute value" },
];

export const MAX_BUDGET_BULK_MODE_OPTIONS = [
  { value: BUDGET_BULK_MODE.COPY_CAMPAIGN_BUDGET, label: "Copy campaign budget exactly" },
  ...BUDGET_BULK_MODE_OPTIONS,
];

export function isBudgetBulkPercentMode(mode) {
  return (
    mode === BUDGET_BULK_MODE.INCREASE_PERCENT ||
    mode === BUDGET_BULK_MODE.DECREASE_PERCENT
  );
}

/** Compute a budget value from a base amount for bulk update modes. */
export function computeBudgetFromBase(baseValue, mode, value) {
  const base = Number(baseValue);
  const safeBase = Number.isFinite(base) ? base : 0;

  if (mode === BUDGET_BULK_MODE.COPY_CAMPAIGN_BUDGET) {
    return roundCampaignBudget(safeBase);
  }

  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return null;

  if (mode === BUDGET_BULK_MODE.INCREASE_PERCENT) {
    return roundCampaignBudget(safeBase * (1 + parsed / 100));
  }
  if (mode === BUDGET_BULK_MODE.DECREASE_PERCENT) {
    return roundCampaignBudget(safeBase * (1 - parsed / 100));
  }
  if (mode === BUDGET_BULK_MODE.SET_ABSOLUTE) {
    return roundCampaignBudget(parsed);
  }
  return null;
}

/** Compute max budget from a campaign's daily budget for bulk update modes. */
export function computeMaxBudgetFromCampaignBudget(dailyBudget, mode, value) {
  return computeBudgetFromBase(dailyBudget, mode, value);
}

/** Allow free typing — block negatives; decimals limited by marketplace scale (0 for JP/ZA). */
export function normalizeBidCapInput(value) {
  if (value === "") return "";
  const { scale } = getSpBidRule();
  let next = String(value);

  if (scale === 0) {
    next = next.replace(/[^\d]/g, "");
    if (next === "") return "";
    const num = Number(next);
    if (Number.isNaN(num)) return "";
    if (num < 0) return "0";
    return next.replace(/^0+(?=\d)/, "");
  }

  // Keep only digits and one decimal point
  next = next.replace(/[^\d.]/g, "");
  const firstDot = next.indexOf(".");
  if (firstDot !== -1) {
    next =
      next.slice(0, firstDot + 1) + next.slice(firstDot + 1).replace(/\./g, "");
    const [whole, frac = ""] = next.split(".");
    next = `${whole}.${frac.slice(0, scale)}`;
  }
  if (next === ".") return "0.";
  const num = Number(next);
  if (next !== "" && !next.endsWith(".") && Number.isNaN(num)) return "";
  if (!next.endsWith(".") && num < 0) return "0";
  return next;
}

export function normalizeMinBudgetInput(value) {
  return normalizeBidCapInput(value);
}

export function normalizeMaxBudgetInput(value) {
  return normalizeBidCapInput(value);
}

/** Unescape API validation messages that may contain literal backslash escapes. */
export function formatValidationIssueMessage(message) {
  if (message == null) return "";
  let text = String(message);
  try {
    if (text.startsWith('"') && text.endsWith('"')) {
      text = JSON.parse(text);
    }
  } catch {
    // use raw string
  }
  return text
    .replace(/\\"/g, '"')
    .replace(/\\'/g, "'")
    .replace(/\\\\/g, "\\")
    .replace(/\\n/g, "\n")
    .replace(/\\t/g, "\t");
}

export function getRuleId(rule) {
  return rule?.id ?? rule?.objectId ?? rule?.object_id;
}

export function getRuleName(rule) {
  return rule?.name ?? "-";
}

export function getRuleUpdatedAt(rule) {
  return rule?.updated_at ?? rule?.updatedAt ?? null;
}

export function getRuleLastRunTime(rule) {
  return rule?.last_run_time ?? rule?.lastRunTime ?? null;
}

export function getScheduleRange(rule) {
  const sc = rule?.schedule_config ?? {};
  return {
    start: sc.start_date ?? "",
    end: sc.end_date ?? "",
  };
}

export function inferAutomationMode(rule) {
  if (rule?.rule_config?.automation_mode) return rule.rule_config.automation_mode;
  const tasks = rule?.rule_tasks ?? [];
  if (tasks.length === 0) return "DAYPARTING";
  const allHaveTimes = tasks.every((t) => {
    const d = t?.rule_action_config?.entity_action_config?.duration_config;
    return d?.start_time && d?.end_time;
  });
  return allHaveTimes ? "DAYPARTING" : "ALL_DAYS_CUSTOM_DAYS";
}

export function getRuleCampaignIds(rule) {
  const ids = new Set();
  (rule?.rule_tasks ?? []).forEach((task) => {
    (task?.entity_configs ?? []).forEach((ec) => {
      if (ec?.entity_type === "campaign" && ec?.id) ids.add(String(ec.id));
    });
  });
  return [...ids];
}

export function getCampaignCount(rule) {
  return getRuleCampaignIds(rule).length;
}

export function getModeLabel(rule) {
  return inferAutomationMode(rule) === "DAYPARTING" ? "Dayparting" : "All / Custom Days";
}

export function formatIsoDateForInput(iso) {
  const s = String(iso ?? "").trim();
  const m = s.match(/^(\d{4}-\d{2}-\d{2})/);
  return m ? m[1] : "";
}

export function selectedDayIndicesFromFrequency(frequencyValues) {
  if (!Array.isArray(frequencyValues) || frequencyValues.length === 0) {
    return [0, 1, 2, 3, 4, 5, 6];
  }
  return frequencyValues
    .map((d) => DAY_NAMES.indexOf(String(d).toLowerCase()))
    .filter((i) => i >= 0)
    .sort((a, b) => a - b);
}

export function frequencyValuesFromDayIndices(indices) {
  if (!indices?.length || indices.length === 7) return null;
  return indices.map((i) => DAY_NAMES[i]);
}

export function mapCampaignStatusForFilter(campaign) {
  const raw = String(campaign?.status ?? "").toLowerCase();
  if (raw === "enabled" || raw === "active" || raw === "live") return "Enabled";
  if (raw === "paused" || raw === "on_hold") return "Paused";
  if (raw === "archived") return "Archived";
  return "Enabled";
}

export function getPortfolioPlatformId(portfolio) {
  const id = portfolio?.platform_id ?? portfolio?.platformId;
  return id == null ? "" : String(id);
}

export function getPortfolioDisplayName(portfolio) {
  return portfolio?.name ?? "-";
}

/** API filters for campaign list search (name), status pills, and optional portfolio. */
export function buildCampaignListApiFilters(
  searchTerm,
  statusFilter,
  portfolioPlatformIds = []
) {
  const filters = [];
  const term = String(searchTerm ?? "").trim();
  if (term) {
    filters.push({ field_name: "name", operator: "CONTAINS", value: term });
  }
  if (statusFilter && statusFilter !== "All") {
    filters.push({
      field_name: "status",
      operator: "EQUALS",
      value: String(statusFilter).toUpperCase(),
    });
  }
  // Backend expects numeric ids (no stringified numbers) for amz_portfolio_id.
  const portfolioIds = [
    ...new Set(
      (portfolioPlatformIds ?? [])
        .map((id) => Number(id))
        .filter((n) => Number.isFinite(n))
    ),
  ];
  if (portfolioIds.length) {
    filters.push({
      field_name: "amz_portfolio_id",
      operator: "IN",
      values: portfolioIds,
    });
  }
  return filters;
}

/** Fetch selected campaigns by mongo id (separate from dropdown search list). */
export function buildSelectedCampaignsApiFilter(campaignIds = []) {
  const ids = [...new Set(campaignIds.map(String).filter(Boolean))];
  if (!ids.length) return [];
  return [{ field_name: "id", operator: "IN", values: ids }];
}

export function isValidCampaignListItem(campaign) {
  return (
    campaign?.status !== "ARCHIVED" &&
    campaign?.platform_id != null &&
    Boolean(getCampaignMongoId(campaign))
  );
}

export function getCampaignMongoId(campaign) {
  return String(campaign?.id ?? campaign?.objectId ?? campaign?._id ?? "");
}

export function getCampaignPlatformId(campaign) {
  return String(campaign?.platform_id ?? campaign?.platformId ?? "");
}

export function getCampaignDisplayName(campaign) {
  return campaign?.name ?? campaign?.campaign_name ?? campaign?.campaignName ?? "-";
}

export function getCampaignBaseBudget(campaign) {
  const v = campaign?.daily_budget ?? campaign?.dailyBudget ?? campaign?.budget ?? 0;
  return Number(v) || 0;
}

/** Green dot for LIVE/active campaigns; grey for expired / other statuses. */
export function isCampaignLiveForIndicator(campaign) {
  const raw = String(campaign?.status ?? "")
    .toUpperCase()
    .replace(/[\s-]+/g, "_");
  return raw === "LIVE" || raw === "ENABLED" || raw === "ACTIVE";
}

export function getCampaignStatusDotColor(campaign) {
  return isCampaignLiveForIndicator(campaign) ? "#22C55E" : "#94A3B8";
}

export function formatDayDisplayName(dayIndex) {
  return DAY_NAMES_DISPLAY[dayIndex] ?? "";
}

export function getCampaignAdSpend(campaign) {
  const v =
    campaign?.ad_spend ??
    campaign?.adSpend ??
    campaign?.spend ??
    campaign?.total_spend ??
    campaign?.daily_budget ??
    campaign?.dailyBudget ??
    campaign?.budget ??
    0;
  return Number(v) || 0;
}

export function formatAdSpendLabel(campaign, currencySymbol = getCampaignBudgetCurrencySymbol()) {
  const amount = getCampaignAdSpend(campaign);
  const locale = getCampaignBudgetNumberLocale();
  const formatted = Number.isInteger(amount)
    ? amount.toLocaleString(locale)
    : amount.toLocaleString(locale, { maximumFractionDigits: 2 });
  return `Daily Budget: ${currencySymbol}${formatted}`;
}
